/**
 * Client helpers for the device-token + notification-preference endpoints.
 * Throws on non-2xx so call sites can surface failures via toast.
 *
 * Also exposes a tiny module-level cache for the current iOS APNS token so
 * the logout flow (in `MobileDrawer` / `LeftSidebar`) can clean up the
 * `user_device_tokens` row owned by the signed-in user before the session
 * ends — important when multiple users share one iPhone.
 */

let cachedDeviceToken = null;

export function setCachedDeviceToken(token) {
  cachedDeviceToken = typeof token === 'string' && token.length > 0 ? token : null;
}

export function getCachedDeviceToken() {
  return cachedDeviceToken;
}

export function clearCachedDeviceToken() {
  cachedDeviceToken = null;
}

async function jsonFetch(url, options = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    // Non-JSON response (e.g. 204) — keep data null.
  }
  if (!res.ok) {
    const err = new Error(data?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function registerDeviceToken({ deviceToken, platform, fcmToken }) {
  return jsonFetch('/api/device-tokens/register', {
    method: 'POST',
    body: JSON.stringify({
      device_token: deviceToken,
      platform,
      fcm_token: fcmToken,
    }),
  });
}

export async function removeDeviceToken(deviceToken) {
  if (!deviceToken) return null;
  return jsonFetch(`/api/device-tokens/${encodeURIComponent(deviceToken)}`, {
    method: 'DELETE',
  });
}

export async function checkTokenRegistration() {
  return jsonFetch('/api/device-tokens/check-registration', { method: 'GET' });
}

export async function updateNotificationPreferences(preferences) {
  return jsonFetch('/api/users/notification-preferences', {
    method: 'PUT',
    body: JSON.stringify({ preferences }),
  });
}

/**
 * Remove the current device's `user_device_tokens` row for the signed-in user.
 * Called from logout handlers before they hit `POST /api/auth/logout`. The
 * server route restricts deletion to the current user's binding, so other
 * users on the same device keep their own rows.
 *
 * Does not touch `notification_preferences` — the user's stored opt-in carries
 * over to their next login on this or any other device.
 */
export async function cleanupDeviceTokenOnLogout() {
  const token = getCachedDeviceToken();
  if (!token) return;
  try {
    await removeDeviceToken(token);
  } catch {
    // Best-effort: never block logout on a token cleanup failure.
  } finally {
    clearCachedDeviceToken();
  }
}
